import React, { useState } from 'react';
import { X, Mail, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { emailReceipt } from '../../api/receipts';

/**
 * EmailReceiptModal Component
 * Modal for sending receipt via email
 */
const EmailReceiptModal = ({ sale, onClose }) => {
  const [email, setEmail] = useState(sale.customer_email || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);

    try {
      await emailReceipt(sale.id, email, message);
      toast.success(`Receipt sent successfully to ${email}`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send receipt');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-lightGray">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-accent-gold" />
            <h2 className="text-lg font-semibold text-primary-black">
              Email Receipt
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-secondary-gray hover:text-primary-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Receipt Info */}
          <div className="bg-secondary-offWhite p-3 rounded-lg mb-4">
            <p className="text-sm text-secondary-gray">Receipt Number</p>
            <p className="font-semibold text-primary-black">{sale.receipt_number}</p>
            <p className="text-sm text-secondary-gray mt-2">Customer</p>
            <p className="font-medium text-primary-black">{sale.customer_name}</p>
          </div>

          {/* Email Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-primary-black mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              required
              disabled={sending}
            />
          </div>

          {/* Optional Message */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-primary-black mb-2">
              Additional Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to the email..."
              rows={4}
              className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
              disabled={sending}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2 border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex-1 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  <span>Send Email</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailReceiptModal;
